import type { ReactNode } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The couple's portrait as a control: a button around the picture, with a scrim
 * and an icon on hover and focus.
 *
 * The picture is left to be a picture. A badge pinned to the circle's edge used
 * to carry the affordance, and it did read on a touch screen where a hover
 * scrim says nothing — but it sat on the corner of every portrait permanently,
 * covering the photo a planner chose in order to advertise a shortcut they only
 * need once. Neither surface depends on it: the create dialog has a labelled
 * "Choose photo" button beside the circle, and the hub header has an Edit
 * button whose dialog owns the photo field. This is the quick way in, not the
 * only way, and a shortcut does not have to announce itself.
 *
 * The scrim confirms it for whoever hovers, `focus-visible` gives the keyboard
 * the same signal, and the button's `aria-label` names the action either way,
 * so nothing here was ever carried by the badge for a screen reader.
 */
const GEOMETRY = {
  /** The create dialog's 96px circle. */
  lg: { icon: 'size-6' },
  /** The hub header's, 64px and 80px from `sm` up. */
  sm: { icon: 'size-5' },
} as const;

export default function CouplePhotoTarget({
  size,
  label,
  onClick,
  busy = false,
  className,
  children,
}: {
  size: keyof typeof GEOMETRY;
  /** The accessible name. Names the action, not the picture — the photo itself
   *  is decorative wherever a heading beside it already names the couple. */
  label: string;
  onClick: () => void;
  busy?: boolean;
  /** The circle's width, from the caller that owns the surrounding layout. */
  className?: string;
  /** The `CouplePhoto` this wraps. */
  children: ReactNode;
}) {
  const g = GEOMETRY[size];

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        aria-label={label}
        aria-busy={busy || undefined}
        disabled={busy}
        onClick={onClick}
        className="group block w-full cursor-pointer rounded-full transition-transform focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none active:scale-[0.98] disabled:cursor-default disabled:active:scale-100"
      >
        {children}
        <span
          className={cn(
            'pointer-events-none absolute inset-1.5 flex items-center justify-center rounded-full bg-foreground/45 transition-opacity duration-150',
            // While a save is in flight the scrim stays up and holds the
            // spinner: the picture underneath is still the old one, and dimming
            // it is what says so.
            busy
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
          )}
        >
          {busy ? (
            <Loader2 className={cn(g.icon, 'animate-spin text-background')} />
          ) : (
            <ImagePlus className={cn(g.icon, 'text-background')} />
          )}
        </span>
      </button>
    </div>
  );
}
